import { motion } from "framer-motion";
import { HelpCircle, MessageSquare, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const faqs = [
  {
    q: "Quel est le délai de livraison ?",
    a: "La formule Starter est prête sous 72h. La formule Clé en main est livrée sous 48h. Pour le Sur-mesure, comptez 5 à 7 jours ouvrés selon la complexité du projet.",
  },
  {
    q: "Dois-je savoir coder ?",
    a: "Non ! Je m'occupe de tout. Vous n'avez qu'à me fournir vos textes, photos et informations. Si vous souhaitez modifier quelque chose plus tard, un guide simple est inclus.",
  },
  {
    q: "Puis-je héberger le site où je veux ?",
    a: "Oui, le portfolio fonctionne sur n'importe quel hébergeur web. Le déploiement est inclus dans la prestation et gratuit sur la plupart des plateformes.",
  },
  {
    q: "Le portfolio est-il vraiment optimisé pour le SEO ?",
    a: "Absolument. Référencement naturel, images optimisées, temps de chargement ultra-rapide — tout est configuré pour que vous soyez visible sur Google dès la mise en ligne. Nos scores Lighthouse sont de 98-100/100.",
  },
  {
    q: "C'est quoi la différence avec un template WordPress ?",
    a: "Un template générique est utilisé par des milliers de personnes. Votre portfolio est unique, ultra-rapide (pas de WordPress = pas de lenteur), et personnalisé à votre image. C'est du sur-mesure, pas du prêt-à-porter. En plus, WordPress charge en moyenne en 3-5 secondes. Notre portfolio se charge en moins d'une seconde.",
  },
  {
    q: "Proposez-vous un remboursement ?",
    a: "Oui, satisfait ou remboursé sous 14 jours si le résultat ne correspond pas à vos attentes. Aucune question posée.",
  },
  {
    q: "Pourquoi c'est aussi peu cher par rapport à un freelance ou une agence ?",
    a: "Notre secret : un template de haute qualité déjà développé, que nous personnalisons à 100% pour vous. Le résultat est identique à un site sur-mesure (même code, mêmes performances), mais le temps de développement est réduit. Vous bénéficiez de la qualité d'une agence au prix d'un template.",
  },
  {
    q: "Est-ce que Wix ou Squarespace ne ferait pas l'affaire ?",
    a: "Wix et Squarespace sont des solutions DIY (vous le faites vous-même). Leur score de performance dépasse rarement 50/100. Notre portfolio obtient 98/100. En termes de design, d'animations et de SEO, l'écart est significatif. Et surtout : vous n'avez rien à faire de votre côté.",
  },
  {
    q: "Qu'est-ce qui est inclus exactement dans la prestation ?",
    a: "Tout ce dont vous avez besoin : intégration de vos textes et photos, personnalisation des couleurs et du design, activation du mode sombre, configuration SEO, déploiement en ligne, et un guide de prise en main. Pour les formules supérieures : traduction bilingue, sections avancées et support prioritaire.",
  },
  {
    q: "Et si j'ai déjà un site web ?",
    a: "Pas de problème ! On peut connecter votre nom de domaine existant à votre nouveau portfolio. Votre ancien site sera remplacé par une version infiniment plus rapide et professionnelle. La transition est transparente.",
  },
  {
    q: "Puis-je voir un exemple avant de commander ?",
    a: "Bien sûr ! La démo live est accessible directement depuis cette page. Elle montre exactement ce que vous obtiendrez, avec les animations, le dark mode, la version mobile — tout est fonctionnel.",
  },
  {
    q: "Qu'est-ce qui n'est PAS inclus ?",
    a: "En toute transparence : le nom de domaine personnalisé (votre-nom.com) nécessite un abonnement mensuel séparé à partir de 9,90€/mois. La rédaction de vos textes n'est pas incluse — vous les fournissez et nous les intégrons. Pour la formule Starter, le bilingue et le SEO avancé ne sont pas inclus.",
  },
];

const SellFAQ = () => {
  return (
    <section className="py-24">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              FAQ complète
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Toutes vos questions, <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">zéro zone d'ombre</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              On joue la transparence totale. Si votre question n'est pas ici, contactez-nous.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm px-6 data-[state=open]:bg-card/80 data-[state=open]:border-primary/30 transition-colors"
                >
                  <AccordionTrigger className="text-left text-base font-medium hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Still have questions CTA */}
          <motion.div
            className="mt-10 p-6 rounded-2xl bg-card/60 border border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">Encore une question ?</p>
                <p className="text-xs text-muted-foreground">Réponse garantie sous 24h</p>
              </div>
            </div>
            <a href="#contact">
              <Button variant="outline" size="sm" className="border-primary/30 hover:bg-primary/10 group">
                Poser ma question
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SellFAQ;
